// scope stack + binding store for mnemonic accelerators.
//
// resolution rules:
//   - a dialog is exclusive: while one is open only its bindings resolve, so
//     Alt+S can never reach a "Search" button behind the modal.
//   - otherwise page bindings resolve first, then global (sidebar) ones.
//
// underline rendering asks isEffective() rather than tracking this itself, so a
// character printed on screen always means "this fires right now".

export const SCOPE_GLOBAL = 'global';
export const SCOPE_PAGE = 'page';
export const SCOPE_DIALOG = 'dialog';

const PRECEDENCE = [SCOPE_DIALOG, SCOPE_PAGE, SCOPE_GLOBAL];

let nextId = 0;
let scopes = [];
let version = 0;
const listeners = new Set();

function emit() {
  version += 1;
  listeners.forEach((fn) => fn());
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const getVersion = () => version;

export function createScope(type = SCOPE_PAGE, label = '') {
  const scope = { id: `${type}-${nextId++}`, type, label, bindings: new Map() };
  scopes.push(scope);
  emit();
  return scope.id;
}

export function removeScope(id) {
  const before = scopes.length;
  scopes = scopes.filter((s) => s.id !== id);
  if (scopes.length !== before) emit();
}

const findScope = (id) => scopes.find((s) => s.id === id);

/**
 * Register a binding. Entries are kept as a list per character so that a
 * duplicate is visible to the dev overlay instead of silently replacing the
 * original, and so unmount removes the right one by identity.
 */
export function register(scopeId, char, entry) {
  const scope = findScope(scopeId);
  if (!scope || !char) return () => {};
  const key = String(char).toLowerCase();
  const list = scope.bindings.get(key) || [];
  const record = { ...entry, char: key, scopeId };
  scope.bindings.set(key, [...list, record]);
  emit();

  return () => {
    const target = findScope(scopeId);
    if (!target) return;
    const current = target.bindings.get(key) || [];
    const next = current.filter((e) => e !== record);
    if (next.length) target.bindings.set(key, next);
    else target.bindings.delete(key);
    emit();
  };
}

/** Scopes eligible to resolve, in priority order. */
function activeScopes() {
  const dialogs = scopes.filter((s) => s.type === SCOPE_DIALOG);
  if (dialogs.length) return [dialogs[dialogs.length - 1]];
  return [...scopes]
    .filter((s) => s.type !== SCOPE_DIALOG)
    .sort((a, b) => PRECEDENCE.indexOf(a.type) - PRECEDENCE.indexOf(b.type));
}

const firstEnabled = (list) => (list || []).find((e) => !e.disabled) || null;

export function resolve(char) {
  if (!char) return null;
  const key = String(char).toLowerCase();
  for (const scope of activeScopes()) {
    const hit = firstEnabled(scope.bindings.get(key));
    if (hit) return hit;
  }
  return null;
}

/** True when this scope's binding for `char` is the one that would actually fire. */
export function isEffective(scopeId, char) {
  if (!scopeId || !char) return false;
  const winner = resolve(char);
  return !!winner && winner.scopeId === scopeId;
}

/** Dev-overlay support: duplicates within a scope, and every live binding. */
export function inspect() {
  return scopes.map((scope) => {
    const entries = [];
    const duplicates = [];
    scope.bindings.forEach((list, char) => {
      entries.push({ char, count: list.length, labels: list.map((e) => e.label || '(unlabelled)') });
      if (list.length > 1) duplicates.push({ char, labels: list.map((e) => e.label || '(unlabelled)') });
    });
    return { id: scope.id, type: scope.type, label: scope.label, entries, duplicates };
  });
}

/** Test-only. */
export function reset() {
  scopes = [];
  nextId = 0;
  emit();
}
